import { put, race, select, take, takeLatest } from "redux-saga/effects";

import { scannerUnexpectedlyDisconnected } from "actions";
import { getConnectedDeviceId } from "selectors";

import {
  DEVICE_CONNECTED,
  DISCONNECT_FROM_DEVICE,
  HUB_DISCONNECTED,
  START_REALTIME_SCAN_WATCHDOG,
  STOP_REALTIME_SCAN_WATCHDOG,
} from "constants/actionTypes";

interface DeviceConnectedAction {
  type: typeof DEVICE_CONNECTED;
  deviceId: string;
  object: {
    device?: { status: string };
    status?: string;
  };
}

/**
 * Block until a scanner becomes the active connected device, or the screen exits.
 * Returns the deviceId once a scanner is connected, or null if the screen was exited.
 */
function* waitForScannerConnection() {
  while (true) {
    const result = yield race({
      deviceEvent: take(DEVICE_CONNECTED),
      stopWatchdog: take(STOP_REALTIME_SCAN_WATCHDOG),
    });

    if (result.stopWatchdog) {
      return null; // Screen exited before a scanner connected
    }

    // Delegate device-type filtering to the already-run reducer.
    // DEVICE_CONNECTED fires for weighbridges and PLCs too; connectedDeviceId
    // is only set for scanners, so null here means keep waiting.
    const deviceId: string | null = yield select(getConnectedDeviceId);
    if (deviceId) {
      return deviceId;
    }
  }
}

enum WatchConnectionResult {
  DEVICE_DISCONNECTED_BY_USER,
  DEVICE_DISCONNECTED_REMOTELY,
  HUB_DISCONNECTED_REMOTELY,
  WATCHDOG_STOPPED,
}

/**
 * Watch a connected scanner until its connection ends or the screen exits.
 * Returns the reason: "unexpected" if the hub dropped it, "user" if disconnected
 * intentionally, or "stop" if the screen was exited.
 */
function* watchConnection(deviceId: string) {
  while (true) {
    const result = yield race({
      hubStatusEvent: take(
        (a: DeviceConnectedAction) =>
          a.type === DEVICE_CONNECTED && a.deviceId === deviceId,
      ),
      userDisconnect: take(
        (a: { type: string; deviceId: string }) =>
          a.type === DISCONNECT_FROM_DEVICE && a.deviceId === deviceId,
      ),
      hubDisconnect: take(HUB_DISCONNECTED),
      stopWatchdog: take(STOP_REALTIME_SCAN_WATCHDOG),
    }); /*) as {
      hubStatusEvent?: DeviceConnectedAction;
      userDisconnect?: unknown;
      hubDisconnect?: unknown;
      stop?: unknown;
    };*/

    if (result.stopWatchdog) {
      return WatchConnectionResult.WATCHDOG_STOPPED;
    }
    if (result.userDisconnect) {
      return WatchConnectionResult.DEVICE_DISCONNECTED_BY_USER;
    }
    if (result.hubDisconnect) {
      return WatchConnectionResult.HUB_DISCONNECTED_REMOTELY;
    }

    const status =
      result.hubStatusEvent!.object?.device?.status ??
      result.hubStatusEvent!.object?.status;

    if (status !== "connected") {
      return WatchConnectionResult.DEVICE_DISCONNECTED_REMOTELY;
    }

    // Treat a "connected" event on an already-monitored device as a no-op and keep watching.
    // Returning here would reset deviceId to null; the outer loop would then stall in
    // waitForScannerConnection because no further DEVICE_CONNECTED event will arrive.
  }
}

function* monitorRealtimeScanning() {
  // Seed the watchdog with any scanner already connected when the screen opens.
  // START_REALTIME_SCAN_WATCHDOG fires on mount; no DEVICE_CONNECTED event
  // will follow for a pre-existing connection, so we must capture it here.
  let deviceId: string | null = yield select(getConnectedDeviceId);

  while (true) {
    if (!deviceId) {
      deviceId = (yield* waitForScannerConnection()) as string | null;
      if (!deviceId) return; // STOP_REALTIME_SCAN_WATCHDOG fired while waiting
    }

    const watchConnectionResult: WatchConnectionResult =
      yield* watchConnection(deviceId);

    if (watchConnectionResult === WatchConnectionResult.WATCHDOG_STOPPED) {
      return;
    }

    if (
      watchConnectionResult ===
      WatchConnectionResult.DEVICE_DISCONNECTED_REMOTELY
    ) {
      yield put(scannerUnexpectedlyDisconnected());
    }

    // Reset to re-enter the wait phase after any disconnect, expected or not.
    // Keeps the watchdog live for the full screen session across multiple
    // connect/disconnect cycles.
    deviceId = null;
  }
}

export default function* watchdogSaga() {
  // Use takeLatest so navigating away and back cancels any lingering monitor
  // before arming a fresh one, preventing overlapping watchers.
  yield takeLatest(START_REALTIME_SCAN_WATCHDOG, monitorRealtimeScanning);
}
